// accountMap — the ONLY file that should need editing when James's real
// Xero export and report template land.
//
// Two plain-data exports:
//   REPORT_LINES   the report's shape, top to bottom
//   ACCOUNT_RULES  ordered account-name → report-line rules
//
// Deliberately NOT AI-driven. Financial figures have to be deterministic and
// auditable — the same export must produce byte-identical numbers every month,
// and every figure must be traceable back to the Xero accounts that fed it.
// Regex rules give us that; an LLM does not.

// ── Report shape ───────────────────────────────────────────────────────
//
// kind:
//   'input'   — summed from the mapped Xero accounts
//   'derived' — computed by `formula` from lines already resolved above it
//   'header'  — presentation only, no value
//
// `formula` receives the metric map `m` keyed by line key, holding every
// line resolved SO FAR (declaration order is evaluation order). It returns a
// number, or null when the figure is undefined (e.g. margin on zero sales).
//
// `sign: -1` marks a line that is *presented* as a positive number but
// *deducted* in the maths — Xero exports discounts and expenses with
// inconsistent polarity depending on the account setup, so reportModel
// normalises every input line to a positive magnitude and the formulas below
// do the subtracting explicitly. That keeps the arithmetic readable.
//
// `emphasis: true` renders bold in the UI and the Excel export (subtotals).
export const REPORT_LINES = [
  { key: 'trading', label: 'Trading', kind: 'header' },

  { key: 'sales', label: 'Sales', kind: 'input', numFmt: 'money' },
  { key: 'discounts', label: 'Discounts', kind: 'input', numFmt: 'money', sign: -1 },
  {
    key: 'net_sales',
    label: 'Net Sales',
    kind: 'derived',
    numFmt: 'money',
    emphasis: true,
    formula: (m) => sub(m.sales, m.discounts),
  },

  { key: 'cogs', label: 'Cost of Goods Sold', kind: 'input', numFmt: 'money', sign: -1 },
  {
    key: 'gross_profit',
    label: 'Gross Profit',
    kind: 'derived',
    numFmt: 'money',
    emphasis: true,
    formula: (m) => sub(m.net_sales, m.cogs),
  },
  {
    key: 'sales_margin',
    label: 'Sales Margin',
    kind: 'derived',
    numFmt: 'pct',
    // Gross Profit over NET sales (post-discount) — the honest margin.
    // See open question 2 in the plan; flip the denominator to m.sales here
    // if James confirms otherwise. Null-guarded: a zero/absent Net Sales
    // yields null (renders '—') rather than Infinity or NaN.
    formula: (m) => (m.net_sales ? m.gross_profit / m.net_sales : null),
  },

  { key: 'expenses', label: 'Expenses', kind: 'header' },

  // Advertising & Marketing is a called-out SUBSET of operating expenses:
  // reportModel excludes these accounts from `opex` so the two lines can be
  // shown side by side without double-counting. See open question 3.
  { key: 'advertising', label: 'Advertising & Marketing', kind: 'input', numFmt: 'money', sign: -1 },
  { key: 'opex', label: 'Operating Expenses', kind: 'input', numFmt: 'money', sign: -1 },
  {
    key: 'total_expenses',
    label: 'Total Expenses',
    kind: 'derived',
    numFmt: 'money',
    emphasis: true,
    formula: (m) => add(m.advertising, m.opex),
  },

  { key: 'result', label: 'Result', kind: 'header' },

  {
    key: 'operating_profit',
    label: 'Operating Profit',
    kind: 'derived',
    numFmt: 'money',
    emphasis: true,
    formula: (m) => sub(m.gross_profit, m.total_expenses),
  },
  {
    key: 'net_profit',
    label: 'Net Profit',
    kind: 'derived',
    numFmt: 'money',
    emphasis: true,
    // Identical to Operating Profit until James's template introduces
    // below-the-line items (interest, tax, depreciation). Kept as its own
    // line because it is what reconciles against Xero's own Net Profit row.
    formula: (m) => m.operating_profit,
  },
  {
    key: 'net_margin',
    label: 'Net Margin',
    kind: 'derived',
    numFmt: 'pct',
    formula: (m) => (m.net_sales ? m.net_profit / m.net_sales : null),
  },
];

// Null-tolerant arithmetic: treat an absent line as 0, but return null when
// BOTH sides are absent, so "no data" stays distinguishable from "zero".
function add(a, b) {
  if (a == null && b == null) return null;
  return (a || 0) + (b || 0);
}
function sub(a, b) {
  if (a == null && b == null) return null;
  return (a || 0) - (b || 0);
}

// Line keys that hold a real figure, in report order. Used by the review UI
// to build its reassignment dropdown, and by reportModel to seed accumulators.
export const INPUT_LINE_KEYS = REPORT_LINES.filter(l => l.kind === 'input').map(l => l.key);

export const LINE_BY_KEY = Object.fromEntries(REPORT_LINES.map(l => [l.key, l]));

// ── Xero section classification ────────────────────────────────────────
//
// Xero financial exports group accounts under bold section headers. The section an
// account sits under is a stronger signal than its name, so we classify the
// section first and use it to pick which rule set applies and what the
// catch-all should be.
const SECTION_KINDS = [
  { kind: 'income', match: [/trading income/i, /^income$/i, /revenue/i, /^sales$/i] },
  { kind: 'cogs', match: [/cost of sales/i, /cost of goods/i, /^direct costs/i] },
  { kind: 'expense', match: [/operating expense/i, /^expenses?$/i, /overhead/i, /administrat/i] },
  { kind: 'other_income', match: [/other income/i, /non-operating income/i] },
  { kind: 'other_expense', match: [/other expense/i, /non-operating expense/i] },
];

export function classifySection(sectionLabel) {
  if (!sectionLabel) return null;
  for (const s of SECTION_KINDS) {
    if (s.match.some(re => re.test(sectionLabel))) return s.kind;
  }
  return null;
}

// ── Account rules ──────────────────────────────────────────────────────
//
// ORDER IS LOAD-BEARING. First match wins, so specific beats general:
// `discounts` MUST precede `sales`, or "Sales Discounts" gets counted as
// revenue and inflates the top line. Same reasoning puts `advertising`
// before the `opex` catch-all.
//
// `section` (optional) restricts a rule to accounts under a matching Xero
// section kind — stops "Freight" in an expense block being read as inbound
// freight (COGS).
//
// `confidence`:
//   'high'   name matched a specific, intentional rule
//   'low'    landed here via a section catch-all — surfaced in the review
//            table so a human confirms it before the report is trusted
export const ACCOUNT_RULES = [
  // ── Discounts — BEFORE sales. Both orderings "work" but only this one
  // is correct, and the failure is silent, so don't reorder.
  {
    line: 'discounts',
    confidence: 'high',
    match: [/discount/i, /rebate/i, /promotion(al)? credit/i, /coupon/i],
  },

  // ── Revenue
  {
    line: 'sales',
    confidence: 'high',
    match: [
      /^sales/i, /revenue/i, /trading income/i,
      /shopify/i, /amazon/i, /wholesale/i, /retail/i,
      /subscription income/i, /marketplace/i,
    ],
  },

  // ── COGS. `section: 'cogs'` scopes the generic names (Freight, Purchases,
  // Packaging) so an expense-block "Freight Out" doesn't land in COGS.
  {
    line: 'cogs',
    confidence: 'high',
    match: [/cost of (goods|sales)/i, /^cogs$/i, /purchases/i, /freight in/i, /inbound freight/i, /duty/i, /customs/i, /packaging/i, /manufactur/i, /^stock /i, /inventory adjust/i],
  },
  {
    line: 'cogs',
    section: 'cogs',
    confidence: 'high',
    match: [/freight/i, /shipping/i, /warehous/i, /fulfil/i, /^landed/i],
  },

  // ── Advertising & Marketing — before the opex catch-all.
  {
    line: 'advertising',
    confidence: 'high',
    match: [
      /advertis/i, /marketing/i, /\bads?\b/i,
      /google ads/i, /meta\b/i, /facebook/i, /instagram/i, /tiktok/i,
      /influencer/i, /affiliate/i, /sponsorship/i, /\bpr\b/i,
      /agency fees/i, /creative/i, /content production/i, /email marketing/i, /klaviyo/i,
    ],
  },

  // ── Operating expenses — named rules first so the report is explainable,
  // then the section catch-all below sweeps up whatever is left.
  {
    line: 'opex',
    confidence: 'high',
    match: [
      /wages/i, /salar/i, /superannuation/i, /payroll/i, /contractor/i, /staff/i, /recruit/i,
      /rent/i, /utilit/i, /electricity/i, /internet/i, /telephone/i, /insurance/i,
      /accounting/i, /bookkeep/i, /audit/i, /legal/i, /consult/i, /professional fees/i,
      /software/i, /subscription/i, /licen[cs]e/i, /\bit\b/i,
      /bank fees/i, /merchant fees/i, /payment processing/i, /stripe/i, /paypal/i, /afterpay/i,
      /freight out/i, /outbound freight/i, /courier/i, /postage/i, /3pl/i,
      /travel/i, /entertain/i, /motor vehicle/i, /repairs/i, /cleaning/i,
      /depreciat/i, /amortis/i, /interest/i, /bad debt/i,
      /office/i, /printing/i, /stationery/i, /general expenses/i, /sundry/i,
    ],
  },

  // ── Section catch-alls. These are why nothing is ever silently dropped:
  // any account under a recognised section that matched no rule above still
  // lands somewhere, flagged 'low' so the review table asks about it.
  { line: 'opex', section: 'expense', confidence: 'low', match: [/.*/] },
  { line: 'opex', section: 'other_expense', confidence: 'low', match: [/.*/] },
  { line: 'cogs', section: 'cogs', confidence: 'low', match: [/.*/] },
  { line: 'sales', section: 'income', confidence: 'low', match: [/.*/] },
  { line: 'sales', section: 'other_income', confidence: 'low', match: [/.*/] },
];

/**
 * Resolve one account row to a report line.
 *
 * @param {string} label       account name as it appears in the export
 * @param {string|null} sectionKind  result of classifySection() for its block
 * @returns {{ line: string, confidence: 'high'|'low' } | null}
 *          null means genuinely unmappable — the review table lists it and
 *          it contributes to nothing until a human assigns it.
 */
export function resolveAccount(label, sectionKind) {
  const name = (label || '').trim();
  if (!name) return null;

  for (const rule of ACCOUNT_RULES) {
    if (rule.section && rule.section !== sectionKind) continue;
    if (rule.match.some(re => re.test(name))) {
      return { line: rule.line, confidence: rule.confidence };
    }
  }
  return null;
}
